import {
  isApiError,
  type CreateSessionResponse,
  type Endpoints,
  type Features,
  type Limits,
} from '@chat-emp/shared';

/**
 * Session token store + reauth coordinator. The chat-emp session JWT lives in
 * memory ONLY — never a cookie, never localStorage. An iframe reload re-runs the
 * handoff, which is cheap and invisible to the user.
 *
 * The token is replaced on a schedule rather than on failure: the server hands
 * back its TTL, so the reactive 401 path is a fallback, not the plan.
 */
let sessionToken: string | null = null;

/**
 * What an account may use before any session says otherwise: nothing. A gated
 * feature must never render and then disappear, so "unknown" and "off" are the
 * same answer here.
 */
export const NO_FEATURES: Features = { booking: false, chatDay: false, contacts: false };

/**
 * The endpoints before any session names them: none. An unconfigured endpoint
 * is an empty string and every reader treats that as "not available", which is
 * the same thing a stale bundle should conclude.
 */
export const NO_ENDPOINTS: Endpoints = { chatDayResponder: '' };

/** The limits before any session names them — the product's own defaults, so a
 *  stale bundle behaves exactly as the constants always did. */
export const NO_LIMITS: Limits = { formMaxFields: 5 };

/** What a redeem tells the app about this account and its surroundings. */
export interface SessionFacts {
  features: Features;
  endpoints: Endpoints;
  limits: Limits;
  /** What session replay records under. Empty from a server that predates the
   *  field, which reads as "do not record" — see `lib/replay.ts`. */
  analyticsId: string;
  /** The session's company, for company-keyed reports. Empty from an older
   *  server, which reads as "do not report" — see `lib/gamification.ts`. */
  companyId: string;
}

/** The BFF's `{ code, message, retryable }` envelope as a thrown error. Lives here, not in
 *  api.ts, because the redeem throws it too and api.ts already imports this module. */
export class ApiRequestError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly retryable: boolean,
    public readonly status: number,
  ) {
    super(message);
  }
}

/** In-flight reauth, shared by all callers so exactly one is triggered at a time. */
let reauth: {
  promise: Promise<void>;
  resolve: () => void;
  timeout: ReturnType<typeof setTimeout>;
} | null = null;

/** Injected by the app: called when a reauth is needed (emits `session-expired`). */
let onReauthNeeded: (() => void) | null = null;

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/**
 * Replace the token at 80% of its life. The server tells us the TTL, so waiting
 * for a 401 to discover it makes the employer pay a mint round-trip we could
 * have spent while they were idle. The remaining 20% is the margin that
 * round-trip has to fit in.
 */
const REFRESH_AT = 0.8;

/**
 * How long a reauth may go unanswered before its waiters are released. A host
 * that cannot mint a handoff (its own session lapsed) would otherwise leave the
 * singleton pending forever, and every later 401 would join a dead promise and
 * hang — no retry, no error, nothing on screen.
 */
const REAUTH_TIMEOUT_MS = 15_000;

/** When the current token is due for replacement (epoch ms); 0 = no session. */
let refreshAt = 0;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;
let visibilityBound = false;

export function getSessionToken(): string | null {
  return sessionToken;
}

export function clearSession(): void {
  sessionToken = null;
  cancelRefresh();
}

/** The host could not mint: release the waiters. True when that leaves no session, i.e. the
 *  cycle was a 401 that already dropped its token; a proactive refresh keeps the live one. */
export function reauthRefused(): boolean {
  cancelRefresh();
  settleReauth();
  return sessionToken === null;
}

function cancelRefresh(): void {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
  refreshAt = 0;
}

/**
 * Arm the next refresh, replacing any prior one so a re-init never stacks two
 * timers against the same session.
 */
function scheduleRefresh(expiresInSeconds: number): void {
  cancelRefresh();
  const ms = expiresInSeconds * 1000 * REFRESH_AT;
  // A zero or nonsensical TTL would arm a 0ms timer that reauths, redeems
  // another one, and reauths again — a hot loop against the host's mint. Better
  // to fall back to the reactive 401 path than to hammer it.
  if (!Number.isFinite(ms) || ms <= 0) return;
  refreshAt = Date.now() + ms;
  refreshTimer = setTimeout(() => void ensureReauth(), ms);
  bindVisibility();
}

/**
 * A timer is not enough on its own: browsers clamp a background tab's timers to
 * >=1min and may suspend them outright — which is exactly the idle-widget case
 * this exists for. So check the wall clock whenever the tab comes back, rather
 * than trusting the refresh to have fired.
 */
function bindVisibility(): void {
  if (visibilityBound || typeof document === 'undefined') return;
  visibilityBound = true;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && refreshAt && Date.now() >= refreshAt) void ensureReauth();
  });
}

/** Register how a reauth is requested from the host (kept out of session.ts so it's testable). */
export function setReauthTrigger(fn: () => void): void {
  onReauthNeeded = fn;
}

/**
 * Exchange a handoff token for a chat-emp session JWT, and satisfy any pending
 * reauth. The handoff token is single-use (atomic consume at ajt2.5), so a given
 * token is redeemed at most once even if this is called repeatedly — e.g. a
 * StrictMode double-mount or a WidgetView remount calling connect() twice. The
 * redemption promise is cached by token; a different (fresh reauth) token
 * redeems anew.
 */
let redeemedToken: string | null = null;
let redeemInFlight: Promise<SessionFacts> | null = null;

export function createSession(handoffToken: string): Promise<SessionFacts> {
  if (handoffToken === redeemedToken && redeemInFlight) return redeemInFlight;
  redeemedToken = handoffToken;
  redeemInFlight = redeemSession(handoffToken);
  return redeemInFlight;
}

async function redeemSession(handoffToken: string): Promise<SessionFacts> {
  try {
    const res = await fetch(`${API_BASE}/api/session`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ handoffToken }),
    }).catch(() => null);
    if (!res) throw new ApiRequestError('network', 'Network error.', true, 0);
    const body: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      if (isApiError(body)) {
        throw new ApiRequestError(body.code, body.message, body.retryable, res.status);
      }
      throw new ApiRequestError('session_failed', 'Request failed.', res.status >= 500, res.status);
    }
    const {
      sessionToken: token,
      expiresInSeconds,
      features,
      endpoints,
      limits,
      analyticsId,
      companyId,
    } = body as CreateSessionResponse;
    sessionToken = token;
    scheduleRefresh(expiresInSeconds);
    // Defaulted because the cast above is a promise, not a guarantee: an older
    // server may omit the feature set, or one feature. Off is the safe way to be wrong.
    return {
      features: { ...NO_FEATURES, ...features },
      endpoints: endpoints ?? NO_ENDPOINTS,
      // A limit that arrives malformed (absent, non-numeric, below 1) is the
      // default, never zero: the safe way to be wrong is the number we shipped.
      limits:
        typeof limits?.formMaxFields === 'number' && limits.formMaxFields >= 1
          ? { formMaxFields: Math.floor(limits.formMaxFields) }
          : NO_LIMITS,
      // Recording under a guessed id is worse than not recording: the replays
      // exist, look fine, and can never be joined to anything.
      analyticsId: typeof analyticsId === 'string' ? analyticsId : '',
      companyId: typeof companyId === 'string' ? companyId : '',
    };
  } catch (err) {
    // Disarm, or this retries forever. scheduleRefresh never ran, so refreshAt
    // is still the old — now past — value, and that is exactly what the
    // visibility check fires on: every subsequent tab focus would mint another
    // handoff and redeem again, unbounded, at whatever moment redeems happen to
    // be failing. No backoff schedule instead: ~20% of the TTL is still on the
    // current token, and when it does run out the reactive 401 path recovers,
    // already bounded by apiFetch's retry-once.
    cancelRefresh();
    throw err;
  } finally {
    // Wake reauth waiters whether or not a token was minted. A failed redeem
    // used to leave them parked forever — the 401 that asked never retried, and
    // because `reauth` stayed non-null no later 401 could start a fresh cycle
    // either. Settling on both paths means a failure retries with no token (a
    // clean 401, not a hang) and the next 401 re-asks. See settleReauth.
    settleReauth();
  }
}

/**
 * End the current reauth cycle, waking everything parked on `ensureReauth`.
 * Nulled so the next 401 opens a new cycle instead of joining a spent one; a
 * successful redeem has already set the token by the time this runs, a failed
 * one leaves it null and the woken caller 401s cleanly.
 *
 * Cancels the give-up timer too: this cycle is settled, and a stray fire would
 * land on whichever cycle happens to be open by then.
 */
function settleReauth(): void {
  if (!reauth) return;
  const pending = reauth;
  reauth = null;
  clearTimeout(pending.timeout);
  pending.resolve();
}

/**
 * Ask the host to re-establish the session and resolve once it has. Concurrent
 * callers share a single request, so only one `session-expired` is emitted per
 * cycle.
 *
 * Two things end a cycle. `settleReauth` covers a redeem that was *attempted* —
 * it fires from redeemSession's `finally`, so success and failure both wake the
 * waiters immediately. REAUTH_TIMEOUT_MS covers the case where no redeem is ever
 * attempted: the host's mint rejected, or it never answered `session-expired` at
 * all, so nothing downstream is ever going to call createSession. Without the
 * timer that cycle stays open forever and every later 401 joins it.
 *
 * Both paths resolve rather than reject: the waiters go on to their retry, which
 * 401s and throws the normal `{ code, message, retryable }` envelope callers
 * already handle. A rejection would be a second failure type for the same
 * condition.
 *
 * Also the proactive path: the refresh timer calls this before the token dies,
 * because the host's answer is the same either way ("mint me a fresh handoff").
 * That makes `session-expired` a slight misnomer when it fires early; it is not
 * worth a second message type and a matching change in every host.
 */
export function ensureReauth(): Promise<void> {
  if (!reauth) {
    let resolve!: () => void;
    const promise = new Promise<void>((r) => (resolve = r));
    const timeout = setTimeout(() => {
      if (reauth?.promise === promise) {
        reauth = null;
        resolve();
      }
    }, REAUTH_TIMEOUT_MS);
    reauth = { promise, resolve, timeout };
    onReauthNeeded?.();
  }
  return reauth.promise;
}
